<template>
  <v-dialog :value="value" persistent scrollable max-width="900" @input="$emit('input', $event)">
    <v-card rounded="0">
      <v-overlay :value="optionsLoading" absolute z-index="5">
        <v-progress-circular indeterminate size="56"></v-progress-circular>
      </v-overlay>

      <v-toolbar color="kenyaGreen" dark flat>
        <v-toolbar-title class="font-weight-bold">
          {{ $t('memberDataSection.buyingStationOrder.bulkImport.purchaseOrder') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon :disabled="loading" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <v-card-text class="px-6 py-5" style="max-height: 72vh">
        <p v-if="addedBy" class="text--secondary mb-5">
          {{ $t('memberDataSection.buyingStationOrder.bulkImport.addedBy') }} {{ addedBy }}
        </p>

        <v-form ref="form">
          <v-row dense>
            <!-- Purchase Date -->
            <v-col cols="12" md="6">
              <label class="d-block mb-1 text-body-2 black--text">
                {{ $t('memberDataSection.buyingStationOrder.bulkImport.purchaseDate') }}<span class="red--text">*</span>
              </label>
              <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.purchaseDate"
                    append-icon="mdi-calendar"
                    outlined
                    dense
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    :error="hasError('purchaseDate')"
                    :messages="messages('purchaseDate')"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="form.purchaseDate" @input="dateMenu = false"></v-date-picker>
              </v-menu>
            </v-col>

            <!-- Factory -->
            <v-col cols="12" md="6">
              <label class="d-block mb-1 text-body-2 black--text">
                {{ $t('memberDataSection.buyingStationOrder.bulkImport.selectFactory') }}<span class="red--text">*</span>
              </label>
              <v-select
                v-model="form.buyingStationId"
                :items="buyingStations"
                item-value="id"
                item-text="name"
                outlined
                dense
                :error="hasError('buyingStationId')"
                :messages="messages('buyingStationId')"
              ></v-select>
            </v-col>

            <!-- Farmer -->
            <v-col cols="12" md="6">
              <label class="d-block mb-1 text-body-2 black--text">
                {{ $t('memberDataSection.buyingStationOrder.bulkImport.selectFarmer') }}<span class="red--text">*</span>
              </label>
              <v-select
                v-model="form.farmerId"
                :items="farmers"
                item-value="id"
                :item-text="farmerText"
                outlined
                dense
                :error="hasError('farmerId')"
                :messages="messages('farmerId')"
                @change="onFarmerSelect"
              ></v-select>
            </v-col>

            <!-- Farm -->
            <v-col cols="12" md="6">
              <label class="d-block mb-1 text-body-2 black--text">
                {{ $t('memberDataSection.buyingStationOrder.bulkImport.selectFarm') }}
              </label>
              <v-select
                v-model="form.farmId"
                :items="farms"
                item-value="id"
                item-text="farmName"
                outlined
                dense
                clearable
                :error="hasError('farmId')"
                :messages="messages('farmId')"
              ></v-select>
            </v-col>

            <!-- Plantation -->
            <v-col cols="12" md="6">
              <label class="d-block mb-1 text-body-2 black--text">
                {{ $t('memberDataSection.buyingStationOrder.bulkImport.selectPlantation') }}
              </label>
              <v-select
                v-model="form.plantationId"
                :items="plantations"
                item-value="id"
                item-text="plantation_name"
                outlined
                dense
                clearable
              ></v-select>
            </v-col>

            <!-- Coffee Species -->
            <v-col cols="12" md="6">
              <label class="d-block mb-1 text-body-2 black--text">
                {{ $t('memberDataSection.buyingStationOrder.bulkImport.coffeeSpecies') }}
              </label>
              <v-select
                v-model="form.speciesId"
                :items="species"
                item-value="id"
                item-text="name"
                outlined
                dense
                clearable
                @change="onSpeciesSelect"
              ></v-select>
            </v-col>

            <!-- Coffee Variety -->
            <v-col cols="12" md="6">
              <label class="d-block mb-1 text-body-2 black--text">
                {{ $t('memberDataSection.buyingStationOrder.bulkImport.coffeeVariety') }}
              </label>
              <v-select
                v-model="form.varietyId"
                :items="varieties"
                item-value="id"
                item-text="name"
                outlined
                dense
                clearable
              ></v-select>
            </v-col>

            <!-- Type of Coffee -->
            <v-col cols="12" md="6">
              <label class="d-block mb-1 text-body-2 black--text">
                {{ $t('memberDataSection.buyingStationOrder.bulkImport.coffeeType') }}
              </label>
              <v-select v-model="form.coffeeTypeId" :items="coffeeTypes" item-value="id" item-text="name" outlined dense clearable></v-select>
            </v-col>

            <!-- Cherry Grade / Quality -->
            <v-col cols="12" md="6">
              <label class="d-block mb-1 text-body-2 black--text">
                {{ $t('memberDataSection.buyingStationOrder.bulkImport.coffeeCherryQuality') }}<span class="red--text">*</span>
              </label>
              <v-select
                v-model="form.coffeeQualityId"
                :items="coffeeCherryQuality"
                item-value="id"
                item-text="name"
                outlined
                dense
                :error="hasError('coffeeCherryQlty')"
                :messages="messages('coffeeCherryQlty')"
              ></v-select>
            </v-col>

            <!-- Quantity -->
            <v-col cols="12" md="6">
              <div class="d-flex justify-space-between align-end mb-1">
                <label class="text-body-2 black--text">
                  {{ $t('memberDataSection.buyingStationOrder.bulkImport.coffeeCherryQuantity') }}<span class="red--text">*</span>
                </label>
                <span class="text-body-2 text--secondary">KG</span>
              </div>
              <v-text-field
                v-model="form.coffeeCherryQty"
                type="number"
                outlined
                dense
                :error="hasError('coffeeCherryQty')"
                :messages="messages('coffeeCherryQty')"
              ></v-text-field>
            </v-col>

            <!-- Price/Kg -->
            <v-col cols="12" md="6">
              <label class="d-block mb-1 text-body-2 black--text">
                {{ $t('memberDataSection.buyingStationOrder.bulkImport.pricePerKg') }}<span class="red--text">*</span>
              </label>
              <v-text-field
                v-model="form.perKgPrice"
                type="number"
                outlined
                dense
                :error="hasError('perKgPrice')"
                :messages="messages('perKgPrice')"
              ></v-text-field>
            </v-col>

            <!-- Receipt No -->
            <v-col cols="12" md="6">
              <label class="d-block mb-1 text-body-2 black--text">
                {{ $t('memberDataSection.buyingStationOrder.bulkImport.receiptNo') }}
              </label>
              <v-text-field
                v-model="form.receiptNo"
                outlined
                dense
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Cherry photo (optional) -->
          <div class="bso-image-box mt-4 d-flex flex-column align-center justify-center py-6">
            <v-img v-if="photoUrl" :src="photoUrl" max-width="140" max-height="140" class="rounded mb-3"></v-img>
            <v-icon v-else size="40" color="grey">mdi-camera-outline</v-icon>
            <input ref="imageInput" type="file" accept="image/*" class="d-none" @change="onImageSelect" />
            <v-btn color="primary" class="white--text mt-3" @click="$refs.imageInput.click()">
              {{ $t('memberDataSection.buyingStationOrder.bulkImport.addImage') }}
            </v-btn>
          </div>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="justify-end py-4 px-6">
        <v-btn outlined color="primary" class="mr-3" min-width="120" :disabled="loading" @click="close">
          {{ $t('cancel') }}
        </v-btn>
        <v-btn color="primary" class="white--text" min-width="120" :loading="loading" @click="save">
          {{ $t('memberDataSection.buyingStationOrder.bulkImport.submit') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CoffeeService from "@/_services/CoffeeService.js";
import PlantationService from "@/_services/PlantationService.js";
import BuyingStationService from "@/_services/BuyingStationService.js";
import { buildRecordUpdatePayload, SERVER_ERROR_CODE_I18N } from "@/utils/buyingStationOrderBulkImport";

const B = "memberDataSection.buyingStationOrder.bulkImport";

export default {
  name: "BuyingStationOrderBulkImportEditDialog",
  props: {
    value: { type: Boolean, default: false },
    record: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    addedBy: { type: String, default: "" },
  },
  data() {
    return {
      dateMenu: false,
      optionsLoading: false,
      buyingStations: [],
      farmers: [],
      farms: [],
      plantations: [],
      species: [],
      varieties: [],
      coffeeTypes: [],
      coffeeCherryQuality: [],
      form: this.emptyForm(),
    };
  },
  computed: {
    getOrganizationId() {
      return this.$store.getters.getUser?.user_organization?.id;
    },
    getSubOrganizationId() {
      return this.$store.getters.getUser?.user_organization?.subOrganizationId;
    },
    isSubEnterprise() {
      return this.$store.getters.isSubEnterprise;
    },
    photoUrl() {
      return this.form.coffeeCherryPicPreview || "";
    },
  },
  watch: {
    value(open) {
      if (open) this.init();
    },
  },
  mounted() {
    if (this.value) this.init();
  },
  methods: {
    emptyForm() {
      return {
        purchaseDate: "",
        buyingStationId: null,
        farmerId: null,
        farmId: null,
        plantationId: null,
        speciesId: null,
        varietyId: null,
        coffeeTypeId: null,
        coffeeQualityId: null,
        coffeeCherryQlty: null,
        coffeeCherryQty: "",
        perKgPrice: "",
        receiptNo: "",
        coffeeCherryPic: null,
        coffeeCherryPicPreview: "",
      };
    },
    async init() {
      this.optionsLoading = true;
      try {
        await Promise.all([this.getBuyingStations(), this.getFarmers(), this.getSpecies(), this.getCoffeeTypes(), this.getQuality()]);
        await this.prefill();
      } catch (e) {
        console.error(e);
      } finally {
        this.optionsLoading = false;
      }
    },
    async prefill() {
      const rec = (this.record && this.record.recordedJsonData) || {};
      const qualityMatch = this.coffeeCherryQuality.find(
        (q) => String(q.name).toLowerCase() === String(rec.coffeeCherryQlty || "").toLowerCase()
      );
      this.form = {
        ...this.emptyForm(),
        purchaseDate:        rec.purchaseDate || "",
        buyingStationId:     rec.buyingStationId || null,
        farmerId:            rec.farmerId   || null,
        farmId:              rec.farmId || null,
        plantationId:        rec.plantationId || null,
        speciesId:           rec.speciesId || null,
        varietyId:           rec.varietyId || null,
        coffeeTypeId:        rec.coffeeTypeId || null,
        coffeeQualityId:     qualityMatch ? qualityMatch.id : null,
        coffeeCherryQlty:    rec.coffeeCherryQlty || null,
        coffeeCherryQty:     rec.coffeeCherryQty != null ? rec.coffeeCherryQty : "",
        perKgPrice:          rec.perKgPrice != null ? rec.perKgPrice : "",
        receiptNo:           rec.receiptNo || "",
        coffeeCherryPic:     rec.coffeeCherryPic || null,
        coffeeCherryPicPreview: rec.coffeeCherryPic
          ? rec.coffeeCherryPic.file_name || rec.coffeeCherryPic.location || ""
          : "",
      };
      const jobs = [];
      if (this.form.farmerId) {
        jobs.push(this.getFarms(this.form.farmerId), this.getPlantations(this.form.farmerId));
      }
      if (this.form.speciesId) jobs.push(this.getVarieties(this.form.speciesId));
      await Promise.all(jobs);
    },
    hasError(field) {
      const md = (this.record && this.record.errorMetadata) || {};
      return Boolean(md[field] && md[field].length);
    },
    messages(field) {
      const md = (this.record && this.record.errorMetadata) || {};
      return (md[field] || [])
        .map((c) => {
          const key = SERVER_ERROR_CODE_I18N[c];
          return key ? this.$t(`${B}.${key}`) : c;
        })
        .filter(Boolean);
    },
    farmerText(item) {
      return `${item.firstName || ""} ${item.lastName || ""}${item.userType === "offline" ? " (O)" : ""}`.trim();
    },
    async getBuyingStations() {
      try {
        const d = await BuyingStationService.listFactories({ page: 1, limit: 500, search: "", order: "ASC" });
        if (d.code === 200) {
          this.buyingStations = (d.data.rows || []).map((u) => ({
            id: u.id,
            name: [u.firstName, u.middleName, u.lastName].filter(Boolean).join(" "),
          }));
        }
      } catch (e) {
        this.buyingStations = [];
      }
    },
    async getFarmers() {
      try {
        await this.$store.dispatch("farmers/fetchFarmersCached", { page: 1, limit: 1000 });
        this.farmers = this.$store.getters["farmers/farmersList"] || [];
      } catch (e) {
        this.farmers = [];
      }
    },
    async getFarms(farmerId) {
      try {
        const d = await BuyingStationService.getFarms(farmerId);
        if (d.code === 200) this.farms = d.data.info || [];
      } catch (e) {
        this.farms = [];
      }
    },
    async getPlantations(farmerId) {
      try {
        const d = await PlantationService.getPlantationData({ page: 1, itemsPerPage: 100, sortBy: [] }, "", "", farmerId);
        if (d.code === 200) this.plantations = d.data.response || [];
      } catch (e) {
        this.plantations = [];
      }
    },
    async getSpecies() {
      try {
        const d = await CoffeeService.getMemberDataCoffeeSpecies(
          this.getOrganizationId,
          this.isSubEnterprise ? this.getSubOrganizationId : null
        );
        if (d.code === 200) {
          const seen = new Set();
          this.species = (d.data.rows || []).filter((i) => {
            const dup = seen.has(i.name);
            seen.add(i.name);
            return !dup;
          });
        }
      } catch (e) {
        this.species = [];
      }
    },
    async getVarieties(speciesId) {
      try {
        const d = await CoffeeService.getMemberDataCoffeeVariety(
          { coffeeSpecies: speciesId },
          this.getOrganizationId,
          this.isSubEnterprise ? this.getSubOrganizationId : null
        );
        if (d.code === 200) this.varieties = d.data.rows || [];
      } catch (e) {
        this.varieties = [];
      }
    },
    async getCoffeeTypes() {
      try {
        const res = await CoffeeService.getCoffeeTypes();
        if (res.code === 200) this.coffeeTypes = res.data || [];
      } catch (e) {
        this.coffeeTypes = [];
      }
    },
    async getQuality() {
      try {
        const d = await CoffeeService.getTreeData(
          "coffeeCherryQuality",
          this.getOrganizationId,
          this.isSubEnterprise ? this.getSubOrganizationId : null
        );
        if (d.code === 200) this.coffeeCherryQuality = d.data.rows || [];
      } catch (e) {
        this.coffeeCherryQuality = [];
      }
    },
    async onFarmerSelect() {
      this.form.farmId = null;
      this.form.plantationId = null;
      this.optionsLoading = true;
      await Promise.all([this.getFarms(this.form.farmerId), this.getPlantations(this.form.farmerId)]);
      this.optionsLoading = false;
    },
    async onSpeciesSelect() {
      this.form.varietyId = null;
      this.optionsLoading = true;
      await this.getVarieties(this.form.speciesId);
      this.optionsLoading = false;
    },
    onImageSelect(event) {
      const file = event.target?.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        this.form.coffeeCherryPic = reader.result;
        this.form.coffeeCherryPicPreview = reader.result;
      };
      reader.readAsDataURL(file);
    },
    close() {
      if (this.loading) return;
      this.$emit("input", false);
    },
    save() {
      const payload = buildRecordUpdatePayload(this.form);
      if (this.form.coffeeCherryPic) payload.coffeeCherryPic = this.form.coffeeCherryPic;
      this.$emit("save", { recordId: this.record.id, payload });
    },
  },
};
</script>

<style lang="scss" scoped>
.bso-image-box {
  border: 1px dashed #bdbdbd;
  border-radius: 8px;
}
</style>
